import type { ReactNode } from "react";
import styles from "./ImportTable.module.css";

export type ImportTableColumn<Row> = {
  id: string;
  header: ReactNode;
  cell: (row: Row) => ReactNode;
  align?: "right";
  width?: string;
  select?: boolean;
};

type ImportTableProps<Row extends { id: string }> = {
  ariaLabel: string;
  columns: ImportTableColumn<Row>[];
  rows: Row[];
  highlightedRowId?: string | null;
  onRowClick?: (row: Row) => void;
};

function columnClass<Row>(column: ImportTableColumn<Row>): string | undefined {
  const classes = [
    column.select ? styles.select : "",
    column.align === "right" ? styles.number : "",
  ]
    .filter(Boolean)
    .join(" ");
  return classes || undefined;
}

const INTERACTIVE_SELECTOR =
  'button, input, label, a, select, textarea, [role="radio"], [role="checkbox"], [data-row-click="ignore"]';

function isInsideInteractiveElement(target: EventTarget): boolean {
  return target instanceof Element && target.closest(INTERACTIVE_SELECTOR) !== null;
}

/** A bordered table with a quiet header and hairline rows. The highlighted row is the one being looked at elsewhere. */
export function ImportTable<Row extends { id: string }>({
  ariaLabel,
  columns,
  rows,
  highlightedRowId = null,
  onRowClick,
}: ImportTableProps<Row>) {
  return (
    <div className={styles.box}>
      <table className={styles.table} aria-label={ariaLabel}>
        <thead>
          <tr>
            {columns.map(column => (
              <th
                key={column.id}
                scope="col"
                className={columnClass(column)}
                style={column.width ? { width: column.width } : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const highlighted = row.id === highlightedRowId;
            return (
              <tr
                key={row.id}
                className={highlighted ? styles.open : undefined}
                aria-current={highlighted || undefined}
                data-clickable={onRowClick ? "true" : undefined}
                onClick={
                  onRowClick
                    ? event => {
                        if (!isInsideInteractiveElement(event.target)) onRowClick(row);
                      }
                    : undefined
                }
              >
                {columns.map(column => (
                  <td
                    key={column.id}
                    className={columnClass(column)}
                    style={column.width ? { width: column.width } : undefined}
                  >
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
